import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";

import { amountInputLabel, amountInputStep, useCurrency } from "../lib/currency";
import type { DropTableDraft, PriceMode } from "../lib/dropTableDraft";

const PRICE_MODE_LABELS: Record<PriceMode, string> = {
  margin: "From margin",
  custom: "Custom price",
  targetPrice: "From target price",
};

/**
 * Where the box price comes from. "From margin" computes it from the expected value, "Custom
 * price" keeps what the operator types, and "From target price" keeps the typed price and lets
 * the server solve two rows' weights until the expected value reaches it.
 */
export function BoxPricingFields({
  draft,
  readOnly,
}: {
  draft: DropTableDraft;
  readOnly?: boolean;
}) {
  const {
    priceMode,
    setPriceMode,
    marginPercent,
    setMarginPercent,
    enteredPrice,
    setEnteredPrice,
  } = draft;
  const currency = useCurrency();

  return (
    <div className="flex flex-wrap items-end gap-5">
      <div className="grid gap-2">
        <Label>Pricing</Label>
        <Select
          value={priceMode}
          onValueChange={(value) => setPriceMode(value as PriceMode)}
          disabled={readOnly}
          items={PRICE_MODE_LABELS}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PRICE_MODE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {priceMode === "custom" ? null : (
        <div className="grid gap-2">
          <Label htmlFor="margin-percent">Margin %</Label>
          <Input
            id="margin-percent"
            className="w-24"
            type="number"
            min={0}
            max={99}
            step={1}
            value={marginPercent}
            onChange={(e) => setMarginPercent(e.target.value)}
            disabled={readOnly}
          />
        </div>
      )}
      {priceMode === "margin" ? null : (
        <div className="grid gap-2">
          <Label htmlFor="entered-price">
            {amountInputLabel(priceMode === "targetPrice" ? "Target price" : "Price", currency)}
          </Label>
          <Input
            id="entered-price"
            className="w-28"
            type="number"
            min={amountInputStep(currency)}
            step={amountInputStep(currency)}
            value={enteredPrice}
            onChange={(e) => setEnteredPrice(e.target.value)}
            disabled={readOnly}
          />
        </div>
      )}
    </div>
  );
}
